import { useMutation, useQuery } from '@apollo/client/react'
import {
  membershipDowngradeSubscription,
  membershipTermDowngradeSubscription,
  subscriptionProductId,
} from '@src/components/sco/UpgradeForm/membershipLadder'
import { CANCEL_SCHEDULED_SUBSCRIPTION_CHANGE } from '@src/gql/mutations/SubscriptionPlanChange'
import { MEMBERSHIP_PLAN_CHANGE } from '@src/gql/queries/MembershipPlanChange'
import type { ScoSubscription } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { useCallback, useMemo, useState } from 'react'

import { STARTUPS_PRO_PRODUCT_ID } from '../intent'
import { pendingPlanChange, subscriptionIsCancelling, switchAtRenewalLabel } from '../planChange'

/**
 * "Keep {plan}": removes a scheduled change, then refetches the viewer so entitlements, the
 * plan cards and every banner reflect the server. Shared by Plans and the billing page.
 */
export function useKeepCurrentPlan(onKept?: () => unknown) {
  const { refetchCurrentUser } = useCurrentUser()
  const [cancelScheduled, state] = useMutation(CANCEL_SCHEDULED_SUBSCRIPTION_CHANGE)
  const [error, setError] = useState<string | null>(null)

  const keep = async (subscriptionId: string) => {
    setError(null)
    try {
      await cancelScheduled({ variables: { input: { subscriptionId } } })
    } catch (err) {
      setError((err as Error)?.message || 'We couldn’t keep your plan. Please try again.')
      return false
    }
    try {
      await Promise.all([refetchCurrentUser?.(), onKept?.()])
    } catch {
      // The mutation already returned the cleared subscription into the cache.
    }
    return true
  }

  return { keep, keeping: state.loading, error }
}

/**
 * What a change to `productId` on `termId` would schedule at renewal, and on which subscription:
 * `plan` for a lower plan (Team → Pro, W4), `term` for the same plan on a shorter billing term
 * (yearly → monthly, L57), `planTerm` for a higher plan on a shorter term (the server schedules
 * that too, so no credit is created).
 */
export type MembershipDowngradeSource = {
  source: ScoSubscription
  kind: 'plan' | 'term' | 'planTerm'
}

/**
 * The viewer's downgrade at renewal: the scheduled change when there is one, otherwise the
 * Team subscription a Team → Pro switch would schedule on (not when it is already set to cancel,
 * which the server refuses). Plans offer Pro with "Switch to Pro at renewal" only in that second
 * case. `downgradeSourceFor` answers the same for any plan and term (L57 term downgrades).
 */
export function useMembershipPlanChange({ skip = false }: { skip?: boolean } = {}) {
  const { currentUser } = useCurrentUser()
  const query = useQuery(MEMBERSHIP_PLAN_CHANGE, {
    skip: skip || !currentUser?.isRegistered,
    fetchPolicy: 'cache-and-network',
  })
  const subscriptions = useMemo(
    () =>
      ((query.data?.viewer?.currentUser?.subscriptions || []) as Array<ScoSubscription | null>).filter(
        (subscription): subscription is ScoSubscription => Boolean(subscription)
      ),
    [query.data?.viewer?.currentUser?.subscriptions]
  )
  const pending = useMemo(() => pendingPlanChange(subscriptions), [subscriptions])
  const downgradeFrom = useMemo(() => {
    if (pending) {
      return null
    }
    const source = membershipDowngradeSubscription({ subscriptions }, STARTUPS_PRO_PRODUCT_ID)
    return source && !subscriptionIsCancelling(source) ? source : null
  }, [pending, subscriptions])
  const keepPlan = useKeepCurrentPlan(() => query.refetch())
  const downgradeSourceFor = useCallback(
    (productId?: string | null, termId?: string | null): MembershipDowngradeSource | null => {
      if (pending) {
        return null
      }
      const held = { subscriptions }
      const lowerPlan = membershipDowngradeSubscription(held, productId)
      if (lowerPlan) {
        return subscriptionIsCancelling(lowerPlan) ? null : { source: lowerPlan, kind: 'plan' }
      }
      const shorterTerm = membershipTermDowngradeSubscription(held, productId, termId)
      if (!shorterTerm || subscriptionIsCancelling(shorterTerm)) {
        return null
      }
      return {
        source: shorterTerm,
        kind: subscriptionProductId(shorterTerm) === productId ? 'term' : 'planTerm',
      }
    },
    [pending, subscriptions]
  )

  const offerProductIds = downgradeFrom ? [STARTUPS_PRO_PRODUCT_ID] : []
  const ctaLabels: Record<string, string> = downgradeFrom
    ? { [STARTUPS_PRO_PRODUCT_ID]: switchAtRenewalLabel(STARTUPS_PRO_PRODUCT_ID) }
    : {}

  return {
    loading: query.loading && !query.data,
    error: query.error,
    pending,
    downgradeFrom,
    downgradeSourceFor,
    subscriptions,
    offerProductIds,
    ctaLabels,
    keepCurrentPlan: () => (pending ? keepPlan.keep(pending.subscriptionId) : Promise.resolve(false)),
    keeping: keepPlan.keeping,
    keepError: keepPlan.error,
    refetch: () => query.refetch(),
  }
}
